// =============================================================================
// FILE: packages/renderer/src/components/vault/VaultSetupModal.tsx
// PURPOSE: First-time vault location picker shown when the user is on the
//          desktop app but hasn't configured a vault yet.
// =============================================================================

import type { JSX } from 'react';
import { useState } from 'react';
import { FolderOpen, Shield, Database, Eye, CheckCircle2, X } from 'lucide-react';
import { vaultStore } from '../../stores/vault.store.js';

export function VaultSetupModal({ onClose, onSetup }: {
  onClose:  () => void;
  onSetup:  (path: string) => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);

  const handleSelect = async () => {
    setBusy(true);
    const vaultPath = await vaultStore.setupVault();
    setBusy(false);
    if (vaultPath) onSetup(vaultPath);
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-zinc-950 border border-teal-500/30 rounded-sm shadow-2xl">

        <div className="px-6 pt-5 pb-3 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-teal-400/70 mb-1">Desktop Feature</div>
            <h2 className="text-[15px] font-bold text-zinc-100">Set Up R2 Project Vault</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-[12.5px] text-zinc-400 leading-relaxed">
            Choose a secure local folder where R2 will organize your project files. Your originals are never moved — R2 creates a structured copy.
          </p>

          {/* Vault structure preview */}
          <div className="p-4 rounded-sm bg-zinc-900/70 border border-zinc-800 font-mono text-[11px] text-zinc-400 space-y-1">
            <div className="text-zinc-200 font-semibold">R2 Vault/</div>
            <div className="ml-4">└── Projects/</div>
            <div className="ml-8">└── <span className="text-teal-400">Project Number</span>/</div>
            <div className="ml-12 text-zinc-500">├── Source Documents/</div>
            <div className="ml-12 text-zinc-500">├── BIM Models/</div>
            <div className="ml-12 text-zinc-500">├── Drawings/</div>
            <div className="ml-12 text-zinc-500">├── Photos/</div>
            <div className="ml-12 text-zinc-500">└── R2 Knowledge Index/</div>
          </div>

          {/* Capability icons */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: Shield,       color: '#5eead4', label: 'Non-destructive', desc: 'Originals never moved' },
              { icon: Database,     color: '#a78bfa', label: 'Auto-indexed',    desc: 'Knowledge graph built' },
              { icon: Eye,          color: '#fcd34d', label: 'Monitored',       desc: 'Change detection' },
            ].map(({ icon: Icon, color, label, desc }) => (
              <div key={label} className="p-3 rounded-sm bg-zinc-900/50 border border-zinc-800 text-center">
                <Icon className="h-4 w-4 mx-auto mb-2" style={{ color }} />
                <div className="text-[11px] font-semibold text-zinc-200">{label}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">{desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-zinc-800 flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-2 text-[11px] uppercase tracking-[0.16em] rounded-sm border border-zinc-700 text-zinc-400 hover:text-zinc-200 transition">
            Skip for now
          </button>
          <button
            data-testid="vault-setup-btn"
            onClick={() => void handleSelect()}
            disabled={busy}
            className="flex-1 flex items-center justify-center gap-2 py-2 text-[11px] uppercase tracking-[0.16em] rounded-sm bg-teal-500/20 border border-teal-500/40 text-teal-300 hover:bg-teal-500/30 transition disabled:opacity-50"
          >
            <FolderOpen className="h-3.5 w-3.5" />
            {busy ? 'Opening…' : 'Choose Vault Location'}
          </button>
        </div>

      </div>
    </div>
  );
}
